// Import the RTK Query methods from the React-specific entry point
import { Paths } from './paths/paths';
import { SignIn, signInWithSessionId } from './request/SignIn';
import { SumsubVerificationToken as SumsubVerificationTokenRequest } from './request/SumsubVerificationToken';
import { SignUp } from './request/SignUp';
import {
  AccessAppRecordStatusEnum,
  User,
  UserBasicTokenResponse,
  UserLoginResponse,
  UserMarketPreferencesResponse,
} from './response/User';
import { cameliseDeep } from '@utils/functions/camelise';
import { apiSlice } from './apiSlice';
import { EmailResendSuccess } from './response/EmailVerification';
import { SumsubVerificationToken } from './response/SumsubVerificationToken';
import { ResetPassword } from './request/ResetPassword';
import {
  UpdatePassword,
  UserInfo,
  UserMarketPreferences,
} from './request/User';
import { AccessToken } from './request/AccessToken';
import { AccessToken as AccessTokenResponse } from './response/AccessToken';
import { UserDocument } from './response/UserDocument';
import { EmailVerification } from './request/EmailVerification';
import { PasswordResetRequest } from './request/PasswordResetRequest';
import {
  encryptedStorage,
  EncryptedStorageKeys,
} from '@setup/encryptedStorage/encryptedStorage';

export const userSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    user: builder.query<User, void>({
      query: () => Paths.GetUser,
      transformResponse: (data: unknown) => cameliseDeep<User>(data),
      providesTags: ['User'],
    }),
    updateUserInfo: builder.mutation<User, UserInfo>({
      query: (data) => ({
        url: Paths.GetUser,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
    accessToken: builder.mutation<AccessTokenResponse, AccessToken>({
      query: (data) => ({
        url: Paths.AccessToken,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (data: unknown) =>
        cameliseDeep<AccessTokenResponse>(data),
    }),
    signUp: builder.mutation<UserBasicTokenResponse, SignUp>({
      query: (data) => ({
        url: Paths.SignUp,
        method: 'POST',
        body: data,
      }),
    }),
    checkLoginStatus: builder.query<AccessAppRecordStatusEnum, SignIn>({
      query: (data) => ({
        url: Paths.CheckLoginStatus,
        method: 'POST',
        body: data,
      }),
    }),
    signIn: builder.mutation<UserLoginResponse, SignIn>({
      query: (data) => ({
        url: Paths.SignIn,
        method: 'POST',
        body: data,
      }),
    }),
    signInWithSessionId: builder.mutation<UserBasicTokenResponse, signInWithSessionId>({
      query: (data) => ({
        url: Paths.SignInWithSessionId,
        method: 'POST',
        body: data,
      }),
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: Paths.Logout,
        method: 'POST',
        body: {
          token: encryptedStorage.getItem(EncryptedStorageKeys.AccessToken),
        },
      }),
    }),
    sendEmailVerificationRequest: builder.mutation<EmailResendSuccess, void>({
      query: () => ({
        url: Paths.SendEmailVerificationRequest,
        method: 'POST',
      }),
    }),
    verifyEmail: builder.mutation<void, EmailVerification>({
      query: (data) => ({
        url: Paths.VerifyEmail,
        method: 'POST',
        body: data,
      }),
    }),
    resetPassword: builder.mutation<void, PasswordResetRequest>({
      query: (data) => ({
        url: Paths.ResetPassword,
        method: 'POST',
        body: data,
      }),
    }),
    getSumsubVerificationTokenRequest: builder.query<
      SumsubVerificationToken,
      SumsubVerificationTokenRequest
    >({
      query: (arg) => {
        const { verificationType } = arg;
        return {
          url: Paths.SumsubVerificationToken,
          params: { verificationType: verificationType },
          method: 'POST',
        };
      },
    }),
    sendPasswordResetRequest: builder.mutation<
      EmailResendSuccess,
      ResetPassword
    >({
      query: (data) => ({
        url: Paths.SendPasswordResetRequest,
        method: 'POST',
        body: data,
      }),
    }),
    updatePassword: builder.mutation<UserBasicTokenResponse, UpdatePassword>({
      query: (data) => ({
        url: Paths.UpdatePassword,
        method: 'PUT',
        body: data,
      }),
    }),
    userDocuments: builder.query<UserDocument[], void>({
      query: () => ({
        url: Paths.UserDocuments,
        method: 'GET',
      }),
    }),
    userMarketPreferences: builder.query<UserMarketPreferencesResponse, void>({
      query: () => Paths.MarketingPreferences,
      providesTags: ['marketing_preferences'],
    }),
    updateUserMarketPreferences: builder.mutation<
      UserMarketPreferencesResponse,
      UserMarketPreferences
    >({
      query: (data) => ({
        url: Paths.MarketingPreferences,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['marketing_preferences'],
    }),
    deleteUserAvatar: builder.mutation<void, void>({
      query: () => ({
        url: Paths.UserAvatar,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

// Export the auto-generated hook for the `getPosts` query endpoint
export const {
  useUserQuery,
  useLazyUserQuery,
  useSignUpMutation,
  useSignInMutation,
  useLazyCheckLoginStatusQuery,
  useSignInWithSessionIdMutation,
  useLogoutMutation,
  useSendEmailVerificationRequestMutation,
  useLazyGetSumsubVerificationTokenRequestQuery,
  useSendPasswordResetRequestMutation,
  useUpdateUserInfoMutation,
  useUpdatePasswordMutation,
  useUserDocumentsQuery,
  useUserMarketPreferencesQuery,
  useUpdateUserMarketPreferencesMutation,
  useAccessTokenMutation,
  useDeleteUserAvatarMutation,
  useVerifyEmailMutation,
  useResetPasswordMutation,
} = userSlice;
